const asBool = (value, fallback = false) => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value).toLowerCase() === "true";
};

const isDev = process.env.NODE_ENV === "development";

export const HOMEPAGE_RANKING_FLAGS = {
  engineEnabled: asBool(process.env.REACT_APP_HOMEPAGE_RANKING_ENGINE, isDev),
  nearMeEnabled: asBool(process.env.REACT_APP_HOMEPAGE_NEAR_ME_ENABLED, isDev),
  noDupEnabled: asBool(process.env.REACT_APP_HOMEPAGE_NO_DUP_ENABLED, isDev),
  diversityEnabled: asBool(
    process.env.REACT_APP_HOMEPAGE_DIVERSITY_RULES_ENABLED,
    isDev,
  ),
};

const resolveFlag = (runtimeValue, defaultValue) => {
  if (typeof runtimeValue === "boolean") return runtimeValue;
  return defaultValue;
};

export const getHomepageRuntimeFlags = (homepageSettings) => ({
  engineEnabled: resolveFlag(
    homepageSettings?.rankingEngineEnabled,
    HOMEPAGE_RANKING_FLAGS.engineEnabled,
  ),
  nearMeEnabled: resolveFlag(
    homepageSettings?.nearMeSectionEnabled,
    HOMEPAGE_RANKING_FLAGS.nearMeEnabled,
  ),
  noDupEnabled: resolveFlag(
    homepageSettings?.noDupEnabled,
    HOMEPAGE_RANKING_FLAGS.noDupEnabled,
  ),
  diversityEnabled: resolveFlag(
    homepageSettings?.diversityEnabled,
    HOMEPAGE_RANKING_FLAGS.diversityEnabled,
  ),
});

const normalizeText = (value) => (value || "").toString().trim().toLowerCase();

const getEntityKey = (item, entityType) => {
  if (!item) return null;

  if (entityType === "shop") {
    return item._id || item.slug || item.businessId?._id || item.businessId?.slug || null;
  }

  if (entityType === "freelancer") {
    return item._id || item.slug || item.userId?._id || null;
  }

  return item._id || null;
};

const getBusinessGroupKey = (item, entityType) => {
  if (!item) return "";

  if (entityType === "shop") {
    return normalizeText(item.businessId?._id || item.businessId?.slug || item.businessId?.name);
  }

  if (entityType === "freelancer") {
    return normalizeText(item.businessId?._id || item.businessId?.name);
  }

  return "";
};

const getCityKey = (item) => normalizeText(item?.city || item?.activeLocationId?.city);

const dedupeStable = (items, entityType, usedKeys) => {
  const unique = [];

  for (const item of items || []) {
    const key = getEntityKey(item, entityType);
    if (!key || usedKeys.has(key)) continue;
    usedKeys.add(key);
    unique.push(item);
  }

  return unique;
};

const applyDiversityStable = (items, entityType, options = {}) => {
  const { cityCap = 2, businessCap = 2 } = options;

  const cityCounts = new Map();
  const businessCounts = new Map();
  const selected = [];
  const overflow = [];

  for (const item of items || []) {
    const cityKey = getCityKey(item);
    const businessKey = getBusinessGroupKey(item, entityType);

    const cityCount = cityKey ? cityCounts.get(cityKey) || 0 : 0;
    const businessCount = businessKey ? businessCounts.get(businessKey) || 0 : 0;

    const cityAllowed = !cityKey || cityCount < cityCap;
    const businessAllowed = !businessKey || businessCount < businessCap;

    if (cityAllowed && businessAllowed) {
      selected.push(item);
      if (cityKey) cityCounts.set(cityKey, cityCount + 1);
      if (businessKey) businessCounts.set(businessKey, businessCount + 1);
    } else {
      overflow.push(item);
    }
  }

  // Preserve capacity by appending overflow in original order.
  return [...selected, ...overflow];
};

export const buildHomepageSectionData = ({
  featuredShops = [],
  featuredFreelancers = [],
  nearShops = [],
  nearFreelancers = [],
  runtimeFlags,
}) => {
  const effectiveFlags = runtimeFlags || HOMEPAGE_RANKING_FLAGS;

  if (!effectiveFlags.engineEnabled) {
    return {
      nearShops,
      nearFreelancers,
      featuredShops,
      featuredFreelancers,
    };
  }

  let computedNearShops = nearShops;
  let computedNearFreelancers = nearFreelancers;
  let computedFeaturedShops = featuredShops;
  let computedFeaturedFreelancers = featuredFreelancers;

  if (effectiveFlags.noDupEnabled) {
    const usedKeys = new Set();

    if (effectiveFlags.nearMeEnabled) {
      computedNearShops = dedupeStable(computedNearShops, "shop", usedKeys);
      computedNearFreelancers = dedupeStable(
        computedNearFreelancers,
        "freelancer",
        usedKeys,
      );
    }

    computedFeaturedShops = dedupeStable(computedFeaturedShops, "shop", usedKeys);
    computedFeaturedFreelancers = dedupeStable(
      computedFeaturedFreelancers,
      "freelancer",
      usedKeys,
    );
  }

  if (effectiveFlags.diversityEnabled) {
    if (effectiveFlags.nearMeEnabled) {
      computedNearShops = applyDiversityStable(computedNearShops, "shop");
      computedNearFreelancers = applyDiversityStable(
        computedNearFreelancers,
        "freelancer",
      );
    }

    computedFeaturedShops = applyDiversityStable(computedFeaturedShops, "shop");
    computedFeaturedFreelancers = applyDiversityStable(
      computedFeaturedFreelancers,
      "freelancer",
    );
  }

  return {
    nearShops: computedNearShops,
    nearFreelancers: computedNearFreelancers,
    featuredShops: computedFeaturedShops,
    featuredFreelancers: computedFeaturedFreelancers,
  };
};
